import {
  createFileRoute,
  useNavigate,
  useSearch
} from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import {
  useGetData,
  useInvalidateQueries,
  usePostData
} from "@/hooks/services/use-query-hooks";
import {
  GetTechnicianScheduleRange,
  UpdateTechnicianScheduleBatch
} from "@/hooks/services/pps/handlers/schedule-api";
import { format, addDays, parseISO } from "date-fns";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Save,
  Calendar,
  Pencil,
} from "lucide-react";
import { TechnicianById } from "@/hooks/services/pps/handlers/technicians-api";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TriggerButtonStyle } from "@/components/tabs/tabs-component";

import {
  CellState,
  buildWeek,
  buildMonth,
  getPeriodDescription,
  normaliseShift
} from "@/routes/plant-3/components/schedule-calendar-utils";
import { ScheduleShiftGrid } from "@/routes/plant-3/components/schedule-shift-grid";
import { ScheduleMonthGrid } from "@/routes/plant-3/components/schedule-month-grid";

// Route search params
interface ScheduleSearchParams {
  technicianId?: string;
  week?: string;
}

export const Route = createFileRoute(
  "/plant-3/(technician-screens)/technicians/schedule"
)({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>): ScheduleSearchParams => ({
    technicianId: (search.technicianId as string) || undefined,
    week: (search.week as string) || undefined
  })
});

function RouteComponent() {
  const [activeView, setActiveView] = useState<"weekly" | "monthly">("weekly");
  const invalidateQueries = useInvalidateQueries();
  const navigate = useNavigate();
  const { technicianId, week } = useSearch({
    from: "/plant-3/(technician-screens)/technicians/schedule"
  });
  const techIdNum = technicianId ? parseInt(technicianId, 10) : undefined;
  const baseDate = week ? parseISO(week) : new Date();
  const daysOfWeek = useMemo(() => buildWeek(baseDate), [baseDate]);
  // Month calendar days (full grid) for monthly view
  const monthDays = useMemo(() => buildMonth(baseDate), [baseDate]);

  // Range start/end depends on active view
  const { rangeStartDateStr, rangeEndDateStr } = useMemo(() => {
    if (activeView === "monthly") {
      const first = monthDays[0];
      const last = monthDays[monthDays.length - 1];
      return {
        rangeStartDateStr: format(first, "yyyy-MM-dd"),
        rangeEndDateStr: format(last, "yyyy-MM-dd")
      };
    }
    return {
      rangeStartDateStr: format(daysOfWeek[0], "yyyy-MM-dd"),
      rangeEndDateStr: format(daysOfWeek[6], "yyyy-MM-dd")
    };
  }, [activeView, daysOfWeek, monthDays]);

  // Week description (e.g. Oct 27-2 Nov 2025 or Nov 3-9 2025)
  const periodDescription = useMemo(
    () => getPeriodDescription(activeView, baseDate, daysOfWeek),
    [activeView, baseDate, daysOfWeek]
  );

  const scheduleHandler = useMemo(
    () =>
      techIdNum
        ? GetTechnicianScheduleRange(techIdNum, {
            startDate: rangeStartDateStr,
            endDate: rangeEndDateStr,
            includeHolidays: true
          })
        : null,
    [techIdNum, rangeStartDateStr, rangeEndDateStr]
  );
  const { data, isLoading, refetch } = useGetData(
    scheduleHandler ||
      GetTechnicianScheduleRange(0, {
        startDate: rangeStartDateStr,
        endDate: rangeEndDateStr
      })
  );

  // Fetch technician detail for header (name, shop) only when id valid
  const technicianDetail = useGetData(
    techIdNum ? TechnicianById(techIdNum) : TechnicianById(-1)
  );

  const [editMode, setEditMode] = useState(false);
  const [grid, setGrid] = useState<Record<string, CellState>>({});
  // Preserve original loaded grid to allow cancel revert
  const [baselineGrid, setBaselineGrid] = useState<Record<string, CellState>>(
    {}
  );
  const [feedback, setFeedback] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  // Initialize grid when data arrives. Map backend shiftType (1/2/3) to AM/PM/OT.
  useEffect(() => {
    if (!data) return;
    const raw = data.data as any[];
    const newGrid: Record<string, CellState> = {};
    raw.forEach((dayDto) => {
      const dateStr: string = dayDto.date;
      const shiftMap: Record<string, { isAvailable: boolean; source: string }> =
        {};
      (dayDto.shifts || []).forEach((s: any) => {
        const code = normaliseShift(s.shift ?? s.shiftType);
        if (!code) return;
        shiftMap[code] = { isAvailable: s.isAvailable, source: s.source };
      });
      (["AM", "PM", "OT"] as const).forEach((shift) => {
        const match = shiftMap[shift];
        // OT (22:00–06:00) is unavailable by default; AM and PM default to available
        const defaultAvailable = shift === "OT" ? false : true;
        const available = match ? match.isAvailable : defaultAvailable;
        const override = match ? match.source === "Override" : false;
        newGrid[`${dateStr}_${shift}`] = {
          available,
          override,
          originalAvailable: available,
          dirty: false
        };
      });
    });
    setGrid(newGrid);
    setBaselineGrid(newGrid);
  }, [data]);

  const toggleCell = (day: string, shift: string) => {
    if (!editMode) return;
    setGrid((g) => {
      const key = `${day}_${shift}`;
      const current = g[key];
      if (!current) return g;
      const updated: CellState = {
        ...current,
        available: !current.available,
        dirty: true
      };
      return { ...g, [key]: updated };
    });
  };

  const goForward = () => {
    let next: Date;
    if (activeView === "monthly") {
      next = new Date(baseDate.getFullYear(), baseDate.getMonth() + 1, 1);
    } else {
      next = addDays(baseDate, 7);
    }
    navigate({
      to: "/plant-3/technicians/schedule",
      search: { technicianId, week: format(next, "yyyy-MM-dd") }
    });
  };
  const goBackward = () => {
    let prev: Date;
    if (activeView === "monthly") {
      prev = new Date(baseDate.getFullYear(), baseDate.getMonth() - 1, 1);
    } else {
      prev = addDays(baseDate, -7);
    }
    navigate({
      to: "/plant-3/technicians/schedule",
      search: { technicianId, week: format(prev, "yyyy-MM-dd") }
    });
  };

  const dirtyItems = useMemo(
    () =>
      Object.entries(grid)
        .filter(([_, v]) => v.dirty && v.originalAvailable !== v.available)
        .map(([k, v]) => {
          const [date, shift] = k.split("_");
          return {
            date,
            shift: shift as "AM" | "PM" | "OT",
            isAvailable: v.available
          };
        }),
    [grid]
  );

  const batchMutation = usePostData(
    techIdNum
      ? UpdateTechnicianScheduleBatch(techIdNum)
      : UpdateTechnicianScheduleBatch(0)
  );

  const saveChanges = useCallback(() => {
    if (!techIdNum || dirtyItems.length === 0) return;
    // Backend expects Items: [{ DateString, ShiftTypeString, IsAvailable, Note? }]
    const payload: any = {
      items: dirtyItems.map((d) => ({
        DateString: d.date,
        ShiftTypeString: d.shift,
        IsAvailable: d.isAvailable,
        Note: "" // note omitted per current UX (placeholder empty)
      }))
    };
    batchMutation.mutate(payload, {
      onSuccess: () => {
        refetch();
        setFeedback({
          type: "success",
          message: "Availability updated successfully."
        });
        // Exit edit mode on success
        setEditMode(false);
        invalidateQueries([["TechnicianList"]]);
        setTimeout(() => {
          setFeedback(null);
        }, 5000);
      },
      onError: (err: any) => {
        const msg = err?.message || "Failed to update availability.";
        setFeedback({ type: "error", message: msg });
        setTimeout(() => setFeedback(null), 7000);
      }
    });
  }, [techIdNum, dirtyItems, batchMutation, refetch]);

  const cancelEdit = () => {
    // revert grid to baseline and exit edit mode
    setGrid(baselineGrid);
    // clear any unsaved feedback about success intentionally left; keep error messages
    setEditMode(false);
  };

  return (
    <div className="aero:flex aero:flex-col aero:gap-4 aero:w-full aero:max-w-full aero:px-4 aero:overflow-hidden">
      {/* Page Header - AeroStaff Style */}
      <div className="aero:flex aero:items-center aero:justify-between aero:min-h-10">
        <div className="aero:flex aero:items-center aero:gap-3">
          <Button
            intent="neutral"
            design="outline"
            size="iconMd"
            shape="rounded"
            onClick={() => navigate({ to: "/plant-3/technicians", search: {} })}
          >
            <ArrowLeft className="aero:h-5 aero:w-5" />
          </Button>
          <div className="aero:w-10 aero:h-10 aero:rounded-lg aero:bg-brand-primary-50 aero:flex aero:items-center aero:justify-center">
            <Calendar className="aero:text-brand-primary-500 aero:h-6 aero:w-6" />
          </div>
          <div>
            <h1 className="aero:text-xl aero:md:text-2xl aero:text-foreground aero:font-bold aero:leading-9">
              {technicianDetail.isLoading
                ? "Loading..."
                : technicianDetail.data?.data?.name || "Technician"}{" "}
              - Schedule
            </h1>
            <p className="aero:text-xs aero:md:text-sm aero:text-neutral-500 aero:leading-5">
              {technicianDetail.data?.data?.shop?.shopName ||
                "Manage technician availability and schedule"}
            </p>
          </div>
        </div>
      </div>

      {/* Main Card */}
      <Card className="aero:!rounded-lg aero:!border-neutral-200 aero:!p-0 aero:!gap-0 aero:!shadow-none">
        <CardHeader className="aero:!p-4">
          <div className="aero:flex aero:flex-col aero:gap-3">
            {/* Navigation and Controls Row */}
            <div className="aero:flex aero:flex-col aero:lg:flex-row aero:items-start aero:lg:items-center aero:justify-between aero:gap-4">
              {/* Left: Navigation */}
              <div className="aero:flex aero:items-center aero:gap-2">
                <Button
                  intent="primary"
                  design="outline"
                  className="aero:h-10 aero:gap-2 aero:rounded aero:px-3 aero:py-2 aero:ml-2"
                  onClick={() => {
                    const today = new Date();
                    navigate({
                      to: "/plant-3/technicians/schedule",
                      search: {
                        technicianId,
                        week: format(today, "yyyy-MM-dd")
                      }
                    });
                  }}
                >
                  Today
                </Button>
                <div className="aero:flex aero:items-center aero:gap-1">
                  <Button
                    intent="secondary"
                    design="text"
                    size="iconSm"
                    shape="rounded"
                    onClick={goBackward}
                  >
                    <ChevronLeft className="aero:h-4 aero:w-4" />
                  </Button>
                  <Button
                    intent="secondary"
                    design="text"
                    size="iconSm"
                    shape="rounded"
                    onClick={goForward}
                  >
                    <ChevronRight className="aero:h-4 aero:w-4" />
                  </Button>
                </div>
                <h2 className="aero:text-base aero:md:text-lg aero:font-semibold aero:text-foreground">
                  {periodDescription}
                </h2>
              </div>

              {/* Right: View Toggle & Actions */}
              <div className="aero:flex aero:items-center aero:gap-3 aero:flex-wrap">
                {/* View Toggle */}
                <Tabs
                  value={activeView}
                  onValueChange={(value) =>
                    setActiveView(value as "weekly" | "monthly")
                  }
                  className="aero:w-auto"
                >
                  <TabsList
                    className={cn(
                      "aero:bg-white aero:rounded-lg aero:border aero:border-neutral-300",
                      "aero:shadow-[0px_4px_4px_0px_rgba(127,127,127,0.1)]",
                      "aero:flex aero:items-center aero:justify-start"
                    )}
                  >
                    <TabsTrigger
                      value="weekly"
                      className={cn(
                        TriggerButtonStyle,
                        "aero:data-[state=active]:bg-brand-primary-50 aero:data-[state=active]:border aero:data-[state=active]:border-brand-primary-700 aero:data-[state=active]:text-brand-primary-700",
                        "aero:data-[state=inactive]:bg-transparent aero:data-[state=inactive]:border-transparent aero:data-[state=inactive]:text-neutral-600"
                      )}
                    >
                      Weekly
                    </TabsTrigger>
                    <TabsTrigger
                      value="monthly"
                      className={cn(
                        TriggerButtonStyle,
                        "aero:data-[state=active]:bg-brand-primary-50 aero:data-[state=active]:border aero:data-[state=active]:border-brand-primary-700 aero:data-[state=active]:text-brand-primary-700",
                        "aero:data-[state=inactive]:bg-transparent aero:data-[state=inactive]:border-transparent aero:data-[state=inactive]:text-neutral-600"
                      )}
                    >
                      Monthly
                    </TabsTrigger>
                  </TabsList>
                </Tabs>

                {/* Edit Actions */}
                {!editMode ? (
                  <Button
                    intent="primary"
                    className="aero:h-10 aero:gap-2 aero:rounded aero:px-3 aero:py-2 aero:ml-2"
                    onClick={() => setEditMode(true)}
                  >
                    <Pencil className="aero:h-4 aero:w-4" />
                    <span>Edit</span>
                  </Button>
                ) : (
                  <div className="aero:flex aero:gap-2">
                    <Button
                      intent="neutral"
                      design="outline"
                      className="aero:h-10 aero:gap-2 aero:rounded aero:px-3 aero:py-2 aero:ml-2"
                      onClick={cancelEdit}
                    >
                      Cancel
                    </Button>
                    <Button
                      intent="primary"
                      className="aero:h-10 aero:gap-2 aero:rounded aero:px-3 aero:py-2 aero:ml-2"
                      onClick={saveChanges}
                      disabled={
                        dirtyItems.length === 0 || batchMutation.isPending
                      }
                    >
                      <Save className="aero:h-4 aero:w-4" />
                      {batchMutation.isPending ? "Saving..." : "Save"}
                      {dirtyItems.length > 0 &&
                        !batchMutation.isPending &&
                        ` (${dirtyItems.length})`}
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Feedback Message */}
            {feedback && (
              <div
                className={cn(
                  "aero:!text-sm aero:rounded-md aero:border aero:px-3 aero:py-2",
                  feedback.type === "success"
                    ? "aero:border-green-300 aero:bg-green-50 aero:!text-green-700"
                    : "aero:border-red-300 aero:bg-red-50 aero:!text-red-700"
                )}
              >
                {feedback.message}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="aero:!p-4 aero:overflow-x-auto">
          {isLoading && (
            <div className="aero:flex aero:items-center aero:justify-center aero:p-8 aero:text-neutral-500">
              Loading schedule...
            </div>
          )}
          {!isLoading && activeView === "weekly" && (
            <ScheduleShiftGrid
              days={daysOfWeek}
              grid={grid}
              editMode={editMode}
              scheduleData={(data?.data as any[]) ?? []}
              toggleCell={toggleCell}
              setGrid={setGrid}
            />
          )}
          {!isLoading && activeView === "monthly" && (
            <ScheduleMonthGrid
              monthDays={monthDays}
              baseDate={baseDate}
              grid={grid}
              editMode={editMode}
              scheduleData={(data?.data as any[]) ?? []}
              toggleCell={toggleCell}
              setGrid={setGrid}
            />
          )}
        </CardContent>

        {/* Legend Section */}
        <div className="aero:border-t aero:border-neutral-200 aero:px-4 aero:py-3">
          <div className="aero:flex aero:flex-wrap aero:gap-4 aero:text-xs aero:text-neutral-600">
            <div className="aero:flex aero:items-center aero:gap-2">
              <div className="aero:w-4 aero:h-4 aero:rounded aero:bg-green-100 aero:border aero:border-green-300" />
              <span>Available</span>
            </div>
            <div className="aero:flex aero:items-center aero:gap-2">
              <div className="aero:w-4 aero:h-4 aero:rounded aero:bg-gray-200 aero:border aero:border-gray-300" />
              <span>Default Unavailable</span>
            </div>
            <div className="aero:flex aero:items-center aero:gap-2">
              <div className="aero:w-4 aero:h-4 aero:rounded aero:bg-red-100 aero:border aero:border-red-300" />
              <span>Override: Unavailable</span>
            </div>
            <div className="aero:flex aero:items-center aero:gap-2">
              <div className="aero:w-4 aero:h-4 aero:rounded aero:border-2 aero:border-blue-500 aero:bg-white" />
              <span>Unsaved Change</span>
            </div>
            <div className="aero:flex aero:items-center aero:gap-2">
              <div className="aero:w-4 aero:h-4 aero:rounded aero:bg-yellow-200 aero:border aero:border-amber-300" />
              <span>Holiday</span>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
